import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box, Card, Grid, Typography, Select, MenuItem, FormControl, InputLabel, Chip, Checkbox, ListItemText
} from '@mui/material'
import { format, subDays } from 'date-fns'

import { Layout } from '_templates'
import { ReportsApi, EngagementApi } from '_services'
import EngagementScoreChart from '../components/organisms/EngagementScoreChart'
import EngagementTimelineGrid from '../components/organisms/EngagementTimelineGrid'

const PLANNING_COLORS = {
  planned_before_day: '#1B5E20',
  planned_early: '#4CAF50',
  mixed: '#FF9800',
  planned_late: '#F44336',
  no_plan: '#9E9E9E',
}

const PLANNING_LABELS = {
  planned_before_day: 'Before Day',
  planned_early: 'Early',
  mixed: 'Mixed',
  planned_late: 'Late',
  no_plan: 'No Plan',
}

const EngagementDashboard = () => {
  const { companyId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [dateRange] = React.useState({
    start: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  })

  const { useGetProjectCompanyQuery } = ReportsApi
  const { data: companyData, isFetching: isLoadingCompany } = useGetProjectCompanyQuery(companyId)

  const { useGetEngagementDashboardQuery } = EngagementApi
  const {
    data: dashboardData,
    isFetching: isLoadingDashboard,
    error: dashboardError,
  } = useGetEngagementDashboardQuery({
    companyId,
    startDate: dateRange.start,
    endDate: dateRange.end,
  })

  const isLoading = isLoadingCompany || isLoadingDashboard

  const projects = useMemo(() => {
    if (!dashboardData?.siteDayScores) return []
    const seen = new Set()
    return dashboardData.siteDayScores
      .filter((s) => {
        if (seen.has(s.projectId)) return false
        seen.add(s.projectId)
        return true
      })
      .map((s) => ({ id: s.projectId, name: s.projectName }))
  }, [dashboardData])

  const selectedProjectIds = useMemo(() => {
    const projectsParam = searchParams.get('projects')
    if (!projectsParam) return []
    return projectsParam
      .split(',')
      .map((id) => String(id.trim()))
      .filter(Boolean)
  }, [searchParams])
  const [pendingProjectIds, setPendingProjectIds] = useState(selectedProjectIds)

  useEffect(() => {
    setPendingProjectIds(selectedProjectIds)
  }, [selectedProjectIds])

  const filteredCraneDays = useMemo(() => {
    if (!dashboardData?.craneDays) return []
    if (!selectedProjectIds.length) return dashboardData.craneDays
    return dashboardData.craneDays.filter((cd) => selectedProjectIds.includes(String(cd.projectId)))
  }, [dashboardData, selectedProjectIds])

  const filteredSiteScores = useMemo(() => {
    if (!dashboardData?.siteDayScores) return []
    if (!selectedProjectIds.length) return dashboardData.siteDayScores
    return dashboardData.siteDayScores.filter((s) => selectedProjectIds.includes(String(s.projectId)))
  }, [dashboardData, selectedProjectIds])

  const regionwide = useMemo(() => {
    if (!dashboardData?.regionwide) return []
    return dashboardData.regionwide
  }, [dashboardData])

  // Compute summary stats
  const summaryStats = useMemo(() => {
    if (!filteredCraneDays.length) return null
    const totalDays = filteredCraneDays.length
    const planned = filteredCraneDays.filter((cd) => cd.planningClassification !== 'no_plan').length
    const avgEngagement = filteredCraneDays.reduce((s, cd) => s + cd.engagementHours, 0) / totalDays
    const avgScore = filteredSiteScores.length > 0
      ? filteredSiteScores.reduce((s, ss) => s + ss.siteScore, 0) / filteredSiteScores.length
      : 0

    const classificationCounts = {}
    filteredCraneDays.forEach((cd) => {
      classificationCounts[cd.planningClassification] = (classificationCounts[cd.planningClassification] || 0) + 1
    })

    return { totalDays, planned, avgEngagement, avgScore, classificationCounts }
  }, [filteredCraneDays, filteredSiteScores])

  const handleProjectChange = (event) => {
    const value = event.target.value
    const nextIds = (typeof value === 'string' ? value.split(',') : value)
      .map((id) => String(id))
      .filter(Boolean)
    setPendingProjectIds(nextIds)
  }

  const applyProjectSelection = () => {
    const nextParams = new URLSearchParams(searchParams)

    if (!pendingProjectIds.length) {
      nextParams.delete('projects')
    } else {
      nextParams.set('projects', pendingProjectIds.join(','))
    }

    setSearchParams(nextParams)
  }

  return (
    <Layout
      title="LNB Engagement Dashboard"
      isLoading={isLoading}
      company={companyData}
      maxWidth="false"
      productType="lnb"
    >
      {!isLoading && dashboardData && (
        <>
          {/* Project filter */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 320, maxWidth: 520 }}>
              <InputLabel>Projects</InputLabel>
              <Select
                multiple
                value={pendingProjectIds}
                label="Projects"
                onChange={handleProjectChange}
                onClose={applyProjectSelection}
                renderValue={(selected) => {
                  if (!selected.length) return 'All Projects'
                  return projects
                    .filter((p) => selected.map((id) => String(id)).includes(String(p.id)))
                    .map((p) => p.name)
                    .join(', ')
                }}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <Checkbox checked={pendingProjectIds.includes(String(p.id))} />
                    <ListItemText primary={p.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Planning classification breakdown */}
          {summaryStats && (
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(summaryStats.classificationCounts).map(([cls, count]) => (
                <Chip
                  key={cls}
                  label={`${PLANNING_LABELS[cls] || cls}: ${count}`}
                  sx={{
                    backgroundColor: PLANNING_COLORS[cls] || '#999',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              ))}
            </Box>
          )}

          <Card sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Engagement Trend</Typography>
            <EngagementScoreChart
              siteDayScores={filteredSiteScores}
              regionwide={regionwide}
            />
          </Card>

          {/* Timeline grid */}
          <Card sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Crane Planning & Engagement Timeline</Typography>
            <EngagementTimelineGrid
              craneDays={filteredCraneDays}
              planningColors={PLANNING_COLORS}
              planningLabels={PLANNING_LABELS}
            />
          </Card>

          {/* Legend */}
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Legend</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" fontWeight="bold">Planning</Typography>
                {Object.entries(PLANNING_LABELS).map(([key, label]) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, backgroundColor: PLANNING_COLORS[key] }} />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" fontWeight="bold">Operator Engagement</Typography>
                <Typography variant="body2">Hours shown per crane-day (capped at 6h for scoring)</Typography>
                <Typography variant="body2" sx={{ mt: 1 }} fontWeight="bold">Score</Typography>
                <Typography variant="body2">Average of planning quality + operator engagement, normalised to 0-100%</Typography>
              </Grid>
            </Grid>
          </Card>
        </>
      )}

      {dashboardError && (
        <Card sx={{ p: 2 }}>
          <Typography color="error">Failed to load engagement dashboard data.</Typography>
        </Card>
      )}
    </Layout>
  )
}

export default EngagementDashboard
